import React, { useEffect, useState } from "react";
import { IRequirementViewModel } from "../../../interfaces/RequirementsInScopeModel";
import { renderDataField, renderFieldLabel } from "./form-fields-renderer"
import { Box, Button, Modal, Typography, Dialog, DialogContent, DialogActions } from '@material-ui/core';
import Spacing from "../../portico/spacing/spacing";
import Accordian from "../../portico/accordion/accordion";
import { styled } from '@mui/material/styles';
import { loadFormFields, parseClassificationAttribute } from "./form-helper-methods";
import CONSTANTS from "../../../utils/constants";
import { JourneyDocument } from "../../../interfaces/JourneyDocument.interface";
import { trimNumbering, filterFields } from "../../../utils/fenergo-utils";
import useDocumentPanelStyles from './documents-panel.styles';
import AccountLookupModal from './AccountLookupModal';
import { useCodatState } from "../../../stores/ui/ui.hooks";
import useFormFieldRendererStyles from './form-fields-renderer.styles';
import closeIcon from "../../../assets/close.svg";
import shieldIcon from "../../../assets/shield-icon-blue.svg";

interface FormDataFieldsProps {
  formMethods: any;
  currentTaskName: string
  watch?: any;
  onChange: any;

  initialValues: any;
  documents: JourneyDocument[]
  journeyId: string;
  journeyName?: string;
  taskId?: string;
  entityId: string;
  entityType: String;

  requirementsInScope: IRequirementViewModel[];
  dynamicDataGroupRequirements: any;
  lookups: Array<object>;
  lookupList: Array<object>;
  lookupLinks: Array<object>;
  dataGroupsSettings: Array<object>;

  loading: boolean;
  readOnly?: boolean;
  labelInsteadOfControl?: boolean;
  collapsedByDefault?: boolean;
  controlledAccordian?: boolean;
  autoExpandCollapesPanels?: boolean;
}

const CustomizedAccordion = styled(Accordian)({
  "& label": {
    fontWeight: 400,
    fontSize: '16px'
  }
});

const DataPullDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paperWidthSm': {
    maxWidth: '536px',
  },
  '& .MuiDialogContent-root': {
    padding: '8px 8px 0px 8px'
  }
}));

export const FormDataFields: React.FC<FormDataFieldsProps> = ({
  formMethods,
  currentTaskName,
  watch,
  onChange, initialValues, documents,
  journeyId, taskId, entityId, entityType, lookups,
  journeyName,
  requirementsInScope, dynamicDataGroupRequirements, lookupList, lookupLinks, dataGroupsSettings,
  loading, readOnly, labelInsteadOfControl, collapsedByDefault,
  controlledAccordian = false,
  autoExpandCollapesPanels = false
}) => {
  const [panels, setPanels] = useState<Array<String>>(null);
  const [showDataDialog, setShowDataDialog] = useState<boolean>(false);
  const [fieldsInPanel, setFieldsInPanel] = useState<object>(null);
  const { submitCount, isSubmitted, isValid } = formMethods.formState;
  const [currentPanelIndex, setCurrentPanelIndex] = useState<number>(0);
  const documentPanelStyles = useDocumentPanelStyles();
  const codatState = useCodatState();
  const formFieldRendererStyles = useFormFieldRendererStyles();

  useEffect(() => {
    if (loading || !requirementsInScope || requirementsInScope.length === 0) return;  
    loadFormFields(initialValues, formMethods, requirementsInScope, setFieldsInPanel, setPanels);

    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, requirementsInScope]);

  useEffect(() => {
    if (isSubmitted && autoExpandCollapesPanels) {
      // isSubmitted needs to used to make sure form submitted
      // autoExpandCollapesPanels props can be used to control this behaviour
      moveToNextPanel();
    }
  }, [submitCount, isSubmitted]);

  const onFieldChange = () => {
    onChange();
  }

  const getPanelFields = (panelName: String) => {
    const fieldsToRender = Object.keys(fieldsInPanel).filter((key) => { return fieldsInPanel[key] === panelName; });

    const dataGroupReqsInScope = requirementsInScope
      .filter((field) => field.dataField?.propertyType === "dataGroup")
      .filter((field) => { return field.entityType === entityType; })
      .filter((field) => {
        return (field.dataField && fieldsToRender.indexOf(field.dataField.propertyName) >= 0)
      });


    return requirementsInScope
      .filter((field) => filterFields(field, entityType, fieldsToRender))
      .sort((req1, req2) => { return req1.order - req2.order })
      .map((field, index) => {
        return renderControl(field, index, dataGroupReqsInScope);
      });
  }

  const [openSyncAccountmodal, setOpenSyncAccountModal] = React.useState(false);

  const handleOpenSyncModal = () => {
    setOpenSyncAccountModal(true);
  }
  const modalTitle = "";

  const renderControl = (field: IRequirementViewModel, index: number, dataGroupReqsInScope: IRequirementViewModel[]) => {
    const fieldName = field.dataField ? field.dataField.propertyName : field.documentDataKey;
    // remve this condition after we move all the compnents to outlined and label inside them
    const outlinedLabel = CONSTANTS.OUTLINE_LABEL_COMPONENTS.indexOf(field?.dataField?.propertyType) > -1;
    return (
      <div key={`${fieldName}-${index}`} className={formFieldRendererStyles.muiInput}>
        {!outlinedLabel || field.isReadOnly && renderFieldLabel(field, formFieldRendererStyles, currentTaskName)}
        {!outlinedLabel && renderFieldLabel(field, formFieldRendererStyles, currentTaskName)}
        {!outlinedLabel && <Spacing variable="spacing-base" />}
        {openSyncAccountmodal &&
          <AccountLookupModal
            fieldName={fieldName}
            onChange={onFieldChange}
            onCloseModal={() => { setOpenSyncAccountModal(false) }}
            journeyId={journeyId}
            journeyName={journeyName}
            taskId={taskId}
            taskName= {currentTaskName}
          />}
        {
          renderDataField(
            fieldName, field, initialValues[fieldName],
            formMethods, onFieldChange,
            loading, readOnly, labelInsteadOfControl,
            journeyId, entityId,
            dataGroupReqsInScope, dynamicDataGroupRequirements,
            lookupList, lookupLinks, dataGroupsSettings,
            initialValues, documents, requirementsInScope, lookups, codatState, handleOpenSyncModal)
        }
         
        {!field.isReadOnly ? <Spacing variable="spacing-base-x3" /> : <Spacing variable="spacing-base-x2" />}

      </div>
    );
  }

  const getDocumentStatus = (panelName) => {
    const fieldsToRender = Object.keys(fieldsInPanel).filter((key) => { return fieldsInPanel[key] === panelName; });
    let docFields = requirementsInScope
      .filter((field) => { return field.entityType === entityType; })
      .filter((field) => {
        return (
          (field.documentDataKey && fieldsToRender.indexOf(field.documentDataKey) >= 0)
        )
      })

    if (panels && documents && docFields.length > 0) {
      let documentStatus = documents.filter(document => document.properties.entityId?.value === entityId)
        .filter(document => document.documentRequirementIds.length > 0 &&
          document.documentRequirementIds[0] === docFields[0].id)
      if (documentStatus.length > 0) {
        return (<Typography className={documentPanelStyles.documentPanelSuccessAdditionalNotes} variant="h2">{CONSTANTS.UPLOAD_SUCCESS}</Typography>);
      } else {
        return (<Typography className={documentPanelStyles.documentPanelFailureAdditionalNotes} variant="h2">{CONSTANTS.UPLOAD_FAILURE}</Typography>);
      }
    }
  }

  const getPanels = () => {
    if (panels) {
      const result = panels.map(panelName => {
        return {
          name: trimNumbering(panelName),
          fields: getPanelFields(panelName),
          status: getDocumentStatus(panelName)
        }
      }).filter((panel) => {
        const fieldsToRender = Object.keys(fieldsInPanel).filter((key) => { return fieldsInPanel[key].indexOf(panel.name) >= 0; });
        return requirementsInScope
          .filter(field => filterFields(field, entityType, fieldsToRender))
          .filter(field => {
            let uiMetaData: any = parseClassificationAttribute(field.classification);
            return uiMetaData && uiMetaData.hideField
          }).length !== fieldsToRender.length;
      });
      return result;
    }
    return [];
  }

  /**
   * Callback function to set the current panel index
   * @param panelIndex panel index that user is currently on
   */
  const onPanelExpand = (panelIndex: number) => {
    setCurrentPanelIndex(panelIndex);
  }

  /**
   * This function moves the user to next panel that HAS errors
   * only when the current panel has NO errors
   */
  // const moveToNextPanel = () => {
  //   // Get panel indexes that have errors
  //   const errorPanelIndexes = [];  
  //   panels.forEach((panelName,index) => {
  //     const fieldsToRender = Object.keys(fieldsInPanel)
  //       .filter((key) => { return fieldsInPanel[key] === panelName; });
  //     const panelHasErrors = Object.keys(formMethods.errors || {})
  //       .some(key => fieldsToRender.some(field => field === key));
  //     if (panelHasErrors)
  //       errorPanelIndexes.push(index); 
  //     }
  //   );

  //   // Determine the next panel that needs to be opened
  //   if (errorPanelIndexes.length > 0 ) {
  //     const currentPanelHasErrors = errorPanelIndexes.indexOf(currentPanelIndex) >= 0; 
  //     // If the current panel that user is on has errors, do not move to next panel
  //     if (!currentPanelHasErrors) {
  //       // If current panel has no errors, open the next panel that has errors
  //       const nextPanelIndexWithErrors = errorPanelIndexes.find(index => index > currentPanelIndex);
  //       if (nextPanelIndexWithErrors) {
  //         // Example: If user is on panel 1 and there are errors on panel 2 and 3, open panel 2
  //         setCurrentPanelIndex(nextPanelIndexWithErrors);
  //       } else {
  //         // Example: If user is on panel 3 and there are errors on panel 1 and 2, open panel 1
  //         setCurrentPanelIndex(errorPanelIndexes[0]);  
  //       }
  //     } 
  //   }
  // }  

  /**
   * This function moves the user to next panel
   * regardless of the current panel's validation
   */
  const moveToNextPanel = () => {
    // Find index in panels that is greater than currentPanelIndex
    const nextPanelIndex = panels.findIndex((panelName, index) => index > currentPanelIndex);
    setCurrentPanelIndex(nextPanelIndex < 0 ? 0 : nextPanelIndex);
  }

  return (
    <>
      {
        CONSTANTS.SHOW_ACCORDION_LAYOUT ?
          <CustomizedAccordion
            controlledAccordian={controlledAccordian}
            panels={getPanels()}
            panelIndexToOpen={currentPanelIndex}
            onPanelExpand={onPanelExpand}></CustomizedAccordion>
          :
          getPanels().map((panel) => {
            return (
              <div key={`${panel.name}`}>
                {currentTaskName === "Financials" && panel.name.trim() === "Sync Your Accounting" ?
                <>
                  <div className={formFieldRendererStyles.syncAccountingTitleWrapper}>
                    <Typography variant="h1">{panel.name}</Typography>
                    <div className={formFieldRendererStyles.securityBadge}>
                      <img alt="shield" src={shieldIcon} />
                      <span style={{ marginLeft: '8px' }}>Secure Encryption</span>
                    </div>
                  </div>
                  <Typography variant="subtitle2" gutterBottom style={{ fontSize: "16px" }}>
                    Connect your <span style={{ fontWeight: 500 }}>Parent Company's</span> accounting platform to avoid needing to manually send us monthly reporting and compliance documents every month (<a style={{ cursor: 'pointer' }} data-testid="what-data-we-pull-link" onClick={() => setShowDataDialog(true)}>What data do we pull?</a>).
                  </Typography>
                </> 
                :
                <Typography variant="h1">{panel.name}</Typography>}
                <Spacing variable="spacing-base-x3" />
                {panel.fields}
                
                <DataPullDialog open={showDataDialog} data-testid="what-data-we-pull-dialog">
                  <DialogContent>
                    <div className={formFieldRendererStyles.dataPullMainContainer}>
                      <a className={formFieldRendererStyles.closeIcon} data-testid="close-dialog-icon" onClick={() => setShowDataDialog(false)}>
                        <img src={closeIcon} />
                      </a>
                      <div className={formFieldRendererStyles.dataPullContentContainer}>
                        <p className={formFieldRendererStyles.dataPullHeading}>What data do we pull?</p>
                        <div className={formFieldRendererStyles.dataPullContents}>
                          <div>We will be pulling the following data from your accounting software each month:</div>
                          <div>
                            <span className={formFieldRendererStyles.dataPullSubHeading}>Financial Statements</span>
                            <ul className={formFieldRendererStyles.dataPullList}>
                              <li>Balance Sheet, Profit & Loss</li>
                            </ul>
                          </div>
                          <div>
                            <span className={formFieldRendererStyles.dataPullSubHeading}>Financial Details</span>
                            <ul className={formFieldRendererStyles.dataPullList}>
                              <li>Chart of Accounts</li>
                            </ul>
                          </div>
                          <div>This information is available from your accounting package. These details help us to better understand your business and provide holistic support.</div>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                  <DialogActions>
                    <Button className={formFieldRendererStyles.closeButton} data-testid="close-dialog-btn" onClick={() => setShowDataDialog(false)}>
                      Close
                    </Button>
                  </DialogActions>
                </DataPullDialog>
              </div>
            )
          })
      }
    </>
  );
};
